/* eslint-disable no-template-curly-in-string */
import { ChipI } from "get-life-storybook-ts/lib/atoms/Chip/Chip";
import { numberFormatPayment, parsePriceDecimal } from "../../../utils/Utils";
import { NewProductI } from "../../../api/request/Lead/Interfaces/ProductInterface.response";
import { GuaranteeOption } from "../Components/GuaranteeToggles/GuaranteeToggles";

interface UseUpsellDisplayDataProps {
  product: NewProductI | undefined;
  translate: (key: string) => string;
  traitLabels: Record<string, string>;
  capitalsByTrait: Record<string, number>;
  traitBoundaries: Array<{ trait: string; available: boolean; min: number; max: number }>;
  traitLoading: boolean;
  isTraitActive: (trait: string) => boolean;
  toggleTrait: (trait: string) => void;
  setTraitCapital: (trait: string, capital: number) => void;
  beforePromoText: string;
  afterPromoText: string;
  couponInfo: any;
  handleConditions: () => void;
}

export const useUpsellDisplayData = ({
  product,
  translate,
  traitLabels,
  capitalsByTrait,
  traitBoundaries,
  traitLoading,
  isTraitActive,
  toggleTrait,
  setTraitCapital,
  beforePromoText,
  afterPromoText,
  couponInfo,
  handleConditions,
}: UseUpsellDisplayDataProps) => {
  const TRAIT_ICONS: Record<string, string> = {
    DEATH: "HeartIcon",
    DISABILITY: "DisabledIcon",
    SERIOUS_ILLNESS: "ShieldCheckIcon",
    MALATTIA_TERMINALE: "ShieldCheckIcon",
  };

  const MANDATORY_TRAITS = ["DEATH", "MALATTIA_TERMINALE"];
  const NON_EDITABLE_TRAITS = ["MALATTIA_TERMINALE"];

  const prepareGuarantees = (): GuaranteeOption[] => {
    const options: GuaranteeOption[] = [];

    for (const boundary of traitBoundaries) {
      if (!boundary.available) continue;
      const isMandatory = MANDATORY_TRAITS.includes(boundary.trait);
      const isActive = isMandatory || isTraitActive(boundary.trait);
      options.push({
        key: boundary.trait,
        label: traitLabels[boundary.trait] ?? boundary.trait,
        icon: TRAIT_ICONS[boundary.trait] ?? "ShieldCheckIcon",
        enabled: !isMandatory,
        active: isActive,
        loading: traitLoading,
        disabledReason: isMandatory ? translate("upsell.guarantees.mandatory") : undefined,
        capital: isActive ? capitalsByTrait[boundary.trait] : undefined,
        minCapital: boundary.min,
        maxCapital: boundary.max,
        editableCapital: !NON_EDITABLE_TRAITS.includes(boundary.trait),
      });
    }

    return options;
  };

  const handleGuaranteeToggle = (key: string) => {
    toggleTrait(key);
  };

  const handleCapitalChange = (key: string, newCapital: number) => {
    setTraitCapital(key, newCapital);
  };

  const buildChipsData = (): ChipI[] => {
    return Object.entries(capitalsByTrait).map(([trait, cap]) => ({
      text: `${traitLabels[trait] ?? trait} ${numberFormatPayment(cap, "capital")} €`,
      type: "success" as const,
      icon: "CheckedIcon",
    }));
  };

  const getDiscountedMonthlyPrice = (): string => {
    if (product?.promotion && product.priceToPay) {
      return numberFormatPayment(product.priceToPay);
    }
    return numberFormatPayment(product?.regularPrice ?? 0);
  };

  const buildPriceData = () => ({
    price: parsePriceDecimal(
      parseFloat(getDiscountedMonthlyPrice().replace(",", ".")),
      undefined
    ),
    priceLabel: translate("calculatorPage.textMonthly"),
    decimals: parsePriceDecimal(
      parseFloat(getDiscountedMonthlyPrice().replace(",", ".")),
      "decimal"
    ),
    frequency: beforePromoText,
    after: afterPromoText,
    PromoOnClick: couponInfo ? handleConditions : undefined,
  });

  return {
    guaranteeOptions: prepareGuarantees(),
    handleGuaranteeToggle,
    handleCapitalChange,
    chipsData: buildChipsData(),
    priceData: buildPriceData(),
    getDiscountedMonthlyPrice,
  };
};
